import type { RoiId } from '~/domain/changes/ids';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import type { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';

/**
 * Pure selection helpers for the structures of the Inside (bounding) and
 * Outside (collision) constraints.
 *
 * These encode the cross-constraint invariants that keep a stroke from being
 * silently erased or bound to a stale ROI. They live outside the React
 * component so the clinically load-bearing rules can be unit-tested directly,
 * rather than only through component effects a future caller could bypass.
 *
 * Each transform returns the SAME array reference when nothing changed, so
 * callers can guard state updates on identity and avoid redundant re-renders.
 */

/**
 * The structures a stroke on `selectedRoiId` can be clipped to or
 * protect: every ROI of the set but the point ROIs, which have no SDF to clip
 * against, and the selected ROI itself, which the engine skips because
 * clipping a structure to itself, or subtracting it from its own stroke,
 * means nothing.
 */
export function constraintCandidates(
    roiArray: readonly RoiReadModel[],
    selectedRoiId: RoiId | null
): RoiReadModel[] {
    return roiArray.filter((r) => !r.isPointRoi && r.roiId !== selectedRoiId);
}

/**
 * The protected structures that apply to a stroke: those among `candidates`.
 * A protected structure that is the selected ROI, or that is no candidate,
 * protects nothing and is left out.
 */
export function protectedCandidateIds(
    protectedRoiIds: readonly RoiId[],
    candidates: readonly RoiReadModel[]
): RoiId[] {
    return protectedRoiIds.filter((n) => candidates.some((r) => r.roiId === n));
}

/**
 * The bounding structure Inside takes when the user turns it on with no
 * structure chosen:
 *  1. The EXTERNAL (Body/External patient contour) structure, if present.
 *  2. A structure literally named "body" or "external" (case-insensitive),
 *     covering data where the interpreted type is not set.
 *  3. Otherwise the alphabetically first candidate.
 *
 * Returns `null` when there are no candidates.
 */
export function pickDefaultBoundingRoi(
    candidates: readonly RoiReadModel[]
): RoiId | null {
    if (candidates.length === 0) {
        return null;
    }
    const external = candidates.find(
        (r) => r.interpretedType === RoiInterpretedType.EXTERNAL
    );
    if (external) {
        return external.roiId;
    }
    const byName = candidates.find((r) => {
        const name = r.name.trim().toLowerCase();
        return name === 'body' || name === 'external';
    });
    if (byName) {
        return byName.roiId;
    }
    const [first] = [...candidates].sort((a, b) =>
        a.name.localeCompare(b.name)
    );
    return first.roiId;
}

/**
 * Inside clips a stroke: it is on, and its structure is among `candidates`.
 * An Inside on with no structure, or bound to the structure being edited,
 * clips nothing.
 */
export function insideConstraintApplies(
    inside: { enabled: boolean; roiId: RoiId | null },
    candidates: readonly RoiReadModel[]
): boolean {
    return inside.enabled && candidates.some((r) => r.roiId === inside.roiId);
}

/**
 * Outside cuts a stroke: it is on, and at least one of its protected
 * structures is among `candidates`.
 */
export function outsideConstraintApplies(
    outside: { enabled: boolean; roiIds: readonly RoiId[] },
    candidates: readonly RoiReadModel[]
): boolean {
    return (
        outside.enabled &&
        protectedCandidateIds(outside.roiIds, candidates).length > 0
    );
}

/**
 * Inside is on but clips nothing: it has no structure, or its structure is
 * not among `candidates`.
 */
export function insideConstraintInactive(
    inside: { enabled: boolean; roiId: RoiId | null },
    candidates: readonly RoiReadModel[]
): boolean {
    return inside.enabled && !insideConstraintApplies(inside, candidates);
}

/**
 * Outside is on but cuts nothing: none of its protected structures is among
 * `candidates`, or it protects none at all.
 */
export function outsideConstraintInactive(
    outside: { enabled: boolean; roiIds: readonly RoiId[] },
    candidates: readonly RoiReadModel[]
): boolean {
    return outside.enabled && !outsideConstraintApplies(outside, candidates);
}

/**
 * Remove the active Inside bound from the protected set. A structure that is
 * both the Inside bound and Outside-protected would be intersected with, then
 * subtracted from, the stroke at clip time — erasing it entirely — so it must
 * never appear in both. (The engine also enforces this; this keeps the UI in
 * sync so the checkbox never shows a self-erasing selection.)
 */
export function dropBoundingFromProtected(
    protectedRoiIds: RoiId[],
    activeBoundingRoi: RoiId | null
): RoiId[] {
    if (
        activeBoundingRoi === null ||
        !protectedRoiIds.includes(activeBoundingRoi)
    ) {
        return protectedRoiIds;
    }
    return protectedRoiIds.filter((n) => n !== activeBoundingRoi);
}

/**
 * Drop protected structures that no longer exist in the active structure set,
 * so a stale selection (e.g. an ROI deleted mid-session, or one of another
 * set) protects nothing rather than lingering in the checked set.
 */
export function pruneProtectedToExisting(
    protectedRoiIds: RoiId[],
    existingRoiIds: Iterable<RoiId>
): RoiId[] {
    const existing = new Set(existingRoiIds);
    const pruned = protectedRoiIds.filter((n) => existing.has(n));
    return pruned.length === protectedRoiIds.length ? protectedRoiIds : pruned;
}

/**
 * Add (checked) or remove (unchecked) every approved structure from the
 * protected set, leaving any manually-checked non-approved structures
 * untouched. Existing entries keep their order; newly added approved entries
 * are appended.
 */
export function toggleProtectAllApproved(
    protectedRoiIds: RoiId[],
    approvedRoiIds: RoiId[],
    checked: boolean
): RoiId[] {
    const set = new Set(protectedRoiIds);
    for (const n of approvedRoiIds) {
        if (checked) {
            set.add(n);
        } else {
            set.delete(n);
        }
    }
    return [...set];
}

/** Tri-state of the "Protect all approved" checkbox. */
export interface ApprovedProtectionState {
    /** At least one approved structure is eligible for protection. */
    hasApproved: boolean;
    /** Every eligible approved structure is currently protected. */
    allApprovedProtected: boolean;
    /** Some (but not necessarily all) approved structures are protected. */
    someApprovedProtected: boolean;
}

/**
 * Derive the "Protect all approved" checkbox state from the eligible approved
 * structures and the current protected selection.
 */
export function computeApprovedProtectionState(
    approvedRoiIds: RoiId[],
    protectedRoiIds: RoiId[]
): ApprovedProtectionState {
    const hasApproved = approvedRoiIds.length > 0;
    const protectedSet = new Set(protectedRoiIds);
    return {
        hasApproved,
        allApprovedProtected:
            hasApproved && approvedRoiIds.every((n) => protectedSet.has(n)),
        someApprovedProtected:
            hasApproved && approvedRoiIds.some((n) => protectedSet.has(n)),
    };
}
